import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { KpiCard } from "./KpiCard";
import { staggerChildren } from "@/lib/motion";

export function RoleDashboard({ data }) {
  const { t } = useTranslation();
  if (!data) return null;
  const { kpis, charts, role, name } = data;

  return (
    <div data-testid={`role-dashboard-${role}`}>
      <div className="mb-7">
        <div className="text-[11px] uppercase tracking-[0.3em] text-sky mb-2">{role}</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wider">
          {t("dashboard.greeting", { name })}
        </h1>
        <p className="text-muted-foreground mt-1">{t("dashboard.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-7">
        <motion.div
          className="contents"
          variants={staggerChildren}
          initial="initial"
          animate="animate"
        >
          {kpis.map((k, i) => (<KpiCard kpi={k} index={i} key={k.key} />))}
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="glass p-5 lg:col-span-1" data-testid="dashboard-donut">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-sm tracking-widest">{t("dashboard.fleetStatus")}</div>
          </div>
          <div className="h-[260px] min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={charts.donut} dataKey="value" nameKey="name" innerRadius={60} outerRadius={92} paddingAngle={2}>
                  {charts.donut.map((d, i) => (<Cell key={i} fill={d.color} />))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "rgba(4,13,28,0.95)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass p-5 lg:col-span-2" data-testid="dashboard-bar">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-sm tracking-widest">{t("dashboard.growth")}</div>
          </div>
          <div className="h-[260px] min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.bar} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="currentColor" tick={{ fontSize: 11 }} />
                <YAxis stroke="currentColor" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(4,13,28,0.95)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }} />
                <Bar dataKey="previous" fill="#003771" radius={[6, 6, 0, 0]} name={t("dashboard.previous")} />
                <Bar dataKey="current" fill="#1E96FC" radius={[6, 6, 0, 0]} name={t("dashboard.current")} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleDashboard;
